import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Typography, Space, Tag, Card, Tooltip, Badge } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { Tags } from "../components/Tags";

interface CorrelationData {
  factor: string;
  impact_on: string;
  relationship: string;
  commentary: string;
  related_factors: string[];
}

// CorrelationAnalysis Block Recipe
const correlationAnalysisRecipe = {
  id: "correlation-analysis-block",
  name: "Correlation Analysis Block",
  globalKey: [],
  prefix: `[
        {
          "factor": "string",
          "impact_on": "string",
          "relationship": "string",
          "commentary": "string",
          "related_factors": ["string"]
        }
    ]`,
  format: {
    type: "json",
    fields: ["factor", "impact_on", "commentary"],
    constraints: [
      { field: "factor", type: "string", required: true },
      { field: "impact_on", type: "string", required: true },
      { field: "commentary", type: "string", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as CorrelationData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) => item.factor && item.impact_on && item.commentary
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// CorrelationAnalysis Block Parser
function correlationAnalysisParser(data: any) {
  try {
    return {
      data: data[correlationAnalysisBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse correlation analysis data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// CorrelationAnalysis Block Variants
const correlationAnalysisVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard correlation analysis display",
    defaultSettings: {},
  },
  {
    id: "modern",
    name: "Modern",
    description: "Modern correlation analysis display",
    defaultSettings: {},
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal correlation analysis display",
    defaultSettings: {},
  },
  {
    id: "impact-only",
    name: "Impact Only",
    description:
      "Show only factor, impact, and a color-coded dot for relationship",
    defaultSettings: {},
  },
  {
    id: "tag-cloud",
    name: "Tag Cloud",
    description:
      "Show each factor as a colored tag by relationship, with tooltip for impact/commentary",
    defaultSettings: {},
  },
];

// CorrelationAnalysis Block Renderer
function CorrelationAnalysisBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const correlationsData = data[correlationAnalysisBlock.id];
  const variantSettings = correlationAnalysisVariants.find(
    (v) => v.id === variant
  )?.defaultSettings;

  if (!correlationsData || correlationsData.length === 0) return null;

  // Variant renderers
  const renderDefaultCard = (correlation: CorrelationData) => (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <Typography.Text className="vzbl-header-5">
        {stripMarkdown(correlation.factor)}
      </Typography.Text>
      <Typography.Text type="secondary">
        <strong>Impact:</strong> {stripMarkdown(correlation.impact_on)}
      </Typography.Text>
      <Typography.Text type="secondary">
        <strong>Relationship:</strong> {stripMarkdown(correlation.relationship)}
      </Typography.Text>
      {correlation.commentary && (
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {correlation.commentary}
        </Markdown>
      )}
      {correlation.related_factors?.length > 0 && (
        <Tags tags={correlation.related_factors} />
      )}
    </Space>
  );

  const renderModernCard = (correlation: CorrelationData) => (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          justifyContent: "space-between",
        }}
      >
        <Typography.Text className="vzbl-header-5">
          {stripMarkdown(correlation.factor)}
        </Typography.Text>
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {stripMarkdown(correlation.relationship)}
        </Tag>
      </div>
      <Typography.Text type="secondary">
        <strong>Impact:</strong> {correlation.impact_on}
      </Typography.Text>
      {correlation.commentary && (
        <Typography.Text
          className="vzbl-text-3 vzbl-clamp-2-lines"
          style={{ color: "var(--color-text-secondary)" }}
        >
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
            className="vzbl-text-3"
          >
            {correlation.commentary}
          </Markdown>
        </Typography.Text>
      )}
      {correlation.related_factors?.length > 0 && (
        <Tags tags={correlation.related_factors} />
      )}
    </Space>
  );

  const renderMinimalCard = (correlation: CorrelationData) => (
    <Space direction="vertical" size={2} style={{ width: "100%" }}>
      <Typography.Text className="vzbl-header-5">
        {stripMarkdown(correlation.factor)}
      </Typography.Text>
      {correlation.commentary && (
        <Typography.Text
          className="vzbl-text-3 vzbl-clamp-2-lines"
          style={{ color: "var(--color-text-secondary)" }}
        >
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
            className="vzbl-text-3"
          >
            {correlation.commentary}
          </Markdown>
        </Typography.Text>
      )}
    </Space>
  );

  const renderImpactOnlyCard = (correlation: CorrelationData) => {
    // Color code by relationship type
    let color = "default";
    if (correlation.relationship.toLowerCase().includes("positive"))
      color = "green";
    else if (correlation.relationship.toLowerCase().includes("negative"))
      color = "red";
    else if (correlation.relationship.toLowerCase().includes("neutral"))
      color = "blue";
    return (
      <Space
        direction="horizontal"
        size="large"
        style={{ width: "100%", alignItems: "center" }}
      >
        <Badge color={color} />
        <Typography.Text className="vzbl-header-5">
          {stripMarkdown(correlation.factor)}
        </Typography.Text>
        <Typography.Text type="secondary">
          <strong>Impact:</strong> {stripMarkdown(correlation.impact_on)}
        </Typography.Text>
      </Space>
    );
  };

  const renderTagCloud = (correlations: CorrelationData[]) => {
    // Color code by relationship type
    const getColor = (relationship: string) => {
      if (relationship.toLowerCase().includes("positive")) return "green";
      if (relationship.toLowerCase().includes("negative")) return "red";
      if (relationship.toLowerCase().includes("neutral")) return "blue";
      return "default";
    };
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {correlations.map((correlation, idx) => (
          <Tooltip
            key={`tagcloud-${idx}`}
            title={
              <div>
                <div>
                  <strong>Impact:</strong> {correlation.impact_on}
                </div>
                {correlation.commentary && (
                  <div style={{ maxWidth: 250 }}>
                    <strong>Note:</strong>
                    <Markdown
                      remarkPlugins={[remarkGfm]}
                      skipHtml={true}
                      className="vzbl-text-3"
                    >
                      {correlation.commentary}
                    </Markdown>
                  </div>
                )}
              </div>
            }
          >
            <Tags tags={[correlation.factor]} />
          </Tooltip>
        ))}
      </div>
    );
  };

  // Main renderer for all variants
  const getCardRenderer = () => {
    switch (variant) {
      case "modern": {
        return renderModernCard;
      }
      case "minimal": {
        return renderMinimalCard;
      }
      case "impact-only": {
        return renderImpactOnlyCard;
      }
      default: {
        return renderDefaultCard;
      }
    }
  };
  const renderCard = getCardRenderer();

  if (variant === "tag-cloud") {
    return (
      <div className="vzbl-panel-container">
        <Typography.Title level={4} className="vzbl-header-4">
          Correlation Analysis
        </Typography.Title>
        {renderTagCloud(correlationsData)}
      </div>
    );
  }

  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Correlation Analysis
      </Typography.Title>
      <div className="responsive-card-grid">
        {correlationsData.map((correlation: CorrelationData, idx: number) => (
          <div
            key={`correlation-${idx}`}
            className="vzbl-panel-shadow vzbl-panel-overlay"
          >
            {renderCard(correlation)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Create CorrelationAnalysis Block
const correlationAnalysisBlock = createBaseBlock({
  id: "correlation-analysis",
  name: "Correlation Analysis Block",
  description: "Displays correlation analysis between factors",
  recipe: correlationAnalysisRecipe,
  variants: correlationAnalysisVariants,
  parser: correlationAnalysisParser,
  renderer: CorrelationAnalysisBlockRenderer,
});

// Export the block for direct use
export { correlationAnalysisBlock };
